import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { motion, AnimatePresence } from 'framer-motion';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { 
  Button, TextField, Paper, Typography, Box, Card, Stack, 
  CircularProgress, ToggleButton, ToggleButtonGroup, Avatar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  Chip, IconButton, GlobalStyles
} from '@mui/material';
import { 
  CalendarMonthRounded as CalendarIcon, 
  FormatListBulletedRounded as TableIcon,
  AutoAwesomeRounded as SparklesIcon,
  EventNoteRounded as EventIcon,
  DeleteSweepRounded as DeleteIcon,
  AddCircleRounded as AddIcon,
  DateRangeRounded as HeaderIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { createBankHoliday, deleteBankHoliday, getBankHolidays } from '../../api/services/Billing/bankHoliday';
import ConfirmModal from '../../components/ConfirmModal';

const BankHolidayManager = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  
  const [view, setView] = useState('calendar');
  const [formData, setFormData] = useState({ name: '', holiday_date: '' });
  const [deleteId, setDeleteId] = useState(null);

  const { data: holidays, isLoading } = useQuery('bankHolidays', getBankHolidays);

  const events = useMemo(() => holidays?.map(h => ({
    id: h.id,
    title: h.name,
    start: h.holiday_date,
    extendedProps: { creator: h.created_by_name || 'System' }
  })) || [], [holidays]);

  const createMutation = useMutation(createBankHoliday, {
    onSuccess: () => {
      queryClient.invalidateQueries('bankHolidays');
      enqueueSnackbar('Holiday successfully deployed', { variant: 'success' });
      setFormData({ name: '', holiday_date: '' });
    },
    onError: (err) => {
      const msg = err.response?.data?.holiday_date ? "This date is already a holiday" : "Failed to save holiday";
      enqueueSnackbar(msg, { variant: 'error' });
    }
  });

  const deleteMutation = useMutation(deleteBankHoliday, {
    onSuccess: () => {
      queryClient.invalidateQueries('bankHolidays');
      enqueueSnackbar('Holiday record archived', { variant: 'info' });
      setDeleteId(null);
    }
  });

  return (
    <Box sx={{ p: 2, background: "linear-gradient(135deg, #F9FAFB 0%, #EFF8FF 100%)", minHeight: "100vh" }}>
      
      <GlobalStyles styles={{
        '.fc': { '--fc-border-color': 'transparent', fontFamily: '"Plus Jakarta Sans", sans-serif' },
        '.fc .fc-scrollgrid': { border: 'none !important' },
        '.fc-theme-standard td, .fc-theme-standard th': { border: 'none !important' },
        '.fc-daygrid-day-frame': { 
          borderRadius: '12px', 
          margin: '2px',
          border: '1px solid rgba(0,0,0,0.03) !important',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': { backgroundColor: '#fff', boxShadow: '0 8px 16px rgba(0,0,0,0.06)', transform: 'translateY(-2px)' } 
        },
        '.fc-day-today .fc-daygrid-day-frame': { backgroundColor: '#F0F7FF !important', borderColor: '#1570EF !important' },
        '.fc-col-header-cell': { paddingBottom: '10px', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 800, color: '#98A2B3' },
        '.fc-event': { background: 'none !important', border: 'none !important' },
        '.fc-toolbar-title': { fontWeight: 800, fontSize: '1.25rem !important', letterSpacing: '-0.5px' },
        '.fc-button-primary': { borderRadius: '8px !important', fontWeight: 600, textTransform: 'capitalize', padding: '4px 10px !important', fontSize: '0.85rem !important' }
      }} />

      {/* Header Section - More Compact */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <motion.div initial={{ rotate: -15 }} animate={{ rotate: 0 }}>
            <Box sx={{
              width: 45, height: 45, display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: '12px', background: 'linear-gradient(135deg, #1570EF 0%, #175CD3 100%)',
              boxShadow: '0 6px 12px rgba(21, 112, 239, 0.2)'
            }}>
              <HeaderIcon sx={{ fontSize: 24, color: '#fff' }} />
            </Box>
          </motion.div>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: '-0.5px' }}>Holiday</Typography>
              <Typography variant="h5" sx={{ fontWeight: 300, color: "#1570EF" }}>Engine</Typography>
              <Chip label="Enterprise" size="small" sx={{ fontWeight: 800, bgcolor: '#E0EAFF', color: '#1570EF', height: 18, fontSize: '10px' }} />
            </Stack>
            <Typography variant="caption" sx={{ color: "#667085", display: 'block' }}>Billing & payroll recalibration.</Typography>
          </Box>
        </Stack>

        <Paper elevation={0} sx={{ p: 0.4, borderRadius: '10px', bgcolor: 'rgba(255, 255, 255, 0.5)', border: '1px solid #EAECF0' }}>
          <ToggleButtonGroup
            value={view}
            exclusive
            size="small"
            onChange={(e, val) => val && setView(val)}
            sx={{ '& .MuiToggleButton-root': { border: 'none', borderRadius: '8px', px: 2, fontSize: '0.75rem' } }}
          >
            <ToggleButton value="calendar"><CalendarIcon sx={{ mr: 1, fontSize: 18 }} /> Visual</ToggleButton>
            <ToggleButton value="table"><TableIcon sx={{ mr: 1, fontSize: 18 }} /> Detailed</ToggleButton>
          </ToggleButtonGroup>
        </Paper>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { md: '280px 1fr' }, gap: 2 }}>
        
        {/* Sidebar - Compact */}
        <Box>
          <Card sx={{ 
            p: 2, borderRadius: '20px', border: '1px solid rgba(255,255,255,0.7)',
            background: 'rgba(255, 255, 255, 0.4)', backdropFilter: 'blur(30px)',
            boxShadow: '0 10px 20px rgba(0,0,0,0.02)'
          }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddIcon color="primary" fontSize="small" /> Register Event
            </Typography>
            <Stack component="form" spacing={1.5} onSubmit={(e) => { e.preventDefault(); createMutation.mutate(formData); }}>
              <TextField
                label="Holiday Name"
                fullWidth
                size="small"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#fff' } }}
              />
              <TextField
                type="date"
                fullWidth
                size="small"
                value={formData.date}
                onChange={(e) => setFormData({...formData, holiday_date: e.target.value})}
                required
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#fff' } }}
              />
              <Button 
                variant="contained" 
                type="submit" 
                fullWidth 
                disabled={createMutation.isLoading}
                sx={{ py: 1, borderRadius: '10px', fontWeight: 700, textTransform: 'none', background: 'linear-gradient(180deg, #448AFF 0%, #1570EF 100%)' }}
              >
                {createMutation.isLoading ? <CircularProgress size={20} color="inherit" /> : "Deploy to Calendar"}
              </Button>
            </Stack>
          </Card>
        </Box>

        {/* Display Area - Compact */}
        <Box>
          <AnimatePresence mode="wait">
            {view === 'calendar' ? (
              <motion.div key="c" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <Paper sx={{ p: 2, borderRadius: '24px', bgcolor: 'rgba(255,255,255,0.8)', boxShadow: '0 20px 40px rgba(0,0,0,0.04)' }}>
                  <FullCalendar
                    plugins={[dayGridPlugin, interactionPlugin]}
                    initialView="dayGridMonth"
                    events={events}
                    dateClick={(i) => setFormData({...formData, date: i.dateStr})}
                    height="580px"
                    eventContent={(eventInfo) => (
                      <motion.div 
                        whileHover={{ y: -1 }}
                        onClick={() => setDeleteId(eventInfo.event.id)}
                        style={{
                          background: 'linear-gradient(135deg, #1570EF 0%, #175CD3 100%)',
                          color: 'white', padding: '4px 8px', borderRadius: '8px',
                          boxShadow: '0 4px 10px rgba(21, 112, 239, 0.2)', cursor: 'pointer',
                          width: '100%', border: '1px solid rgba(255,255,255,0.1)'
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.65rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {eventInfo.event.title}
                        </Typography>
                      </motion.div>
                    )}
                  />
                </Paper>
              </motion.div>
            ) : (
              <motion.div key="t" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <TableContainer component={Paper} sx={{ borderRadius: '24px', boxShadow: '0 20px 40px rgba(0,0,0,0.04)' }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: "#F9FAFB" }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800, py: 1.5, px: 2, fontSize: '0.75rem' }}>Event Name</TableCell>
                        <TableCell sx={{ fontWeight: 800, fontSize: '0.75rem' }}>Holiday Date</TableCell>
                        <TableCell sx={{ fontWeight: 800, fontSize: '0.75rem' }}>Authorized By</TableCell>
                        <TableCell align="right" sx={{ pr: 2, fontSize: '0.75rem' }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {holidays?.map((h) => (
                        <TableRow key={h.id} hover>
                          <TableCell sx={{ px: 2, py: 0.5 }}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Avatar sx={{ width: 28, height: 28, bgcolor: '#F0F9FF', color: '#1570EF', borderRadius: '8px' }}><EventIcon sx={{ fontSize: 16 }} /></Avatar>
                              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{h.name}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ py: 0.5 }}>
                            <Chip label={h.holiday_date} size="small" sx={{ fontWeight: 700, borderRadius: '6px', fontSize: '0.7rem' }} />
                          </TableCell>
                          <TableCell sx={{ py: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#667085', fontSize: '0.7rem' }}>{h.created_by_name || 'System'}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ pr: 2, py: 0.5 }}>
                            <IconButton size="small" onClick={() => setDeleteId(h.id)} sx={{ color: '#FDA29B' }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </Box>

      <ConfirmModal
        show={!!deleteId}
        handleClose={() => setDeleteId(null)}
        handleConfirm={() => deleteMutation.mutate(deleteId)}
        isLoading={deleteMutation.isLoading}
        isDelete={true}
        title="Archive Record?"
        body="Standard rates will revert for this date."
      />
    </Box>
  );
};

export default BankHolidayManager;